import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import PropTypes from 'prop-types';

export default function AddEditStudentModal({ 
  open, 
  onOpenChange, 
  student = null, 
  batches = [],
  courses = [],
  onSave 
}) {
  const isEditMode = !!student;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNo: "",
    courseId: courses.length > 0 ? courses[0]?.id || "none" : "none",
    batchName: "none",
    courseUrl: "",
    courseIdValue: ""
  });
  
  // ✅ FIXED: Add proper cleanup when modal opens/closes
  useEffect(() => {
    if (open) {
      // Focus the first input when modal opens
      setTimeout(() => {
        const firstInput = document.querySelector('#name');
        if (firstInput) {
          firstInput.focus();
        }
      }, 100);
    }
  }, [open]);
  
  // ✅ FIXED: Add cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup function - reset form data
      setFormData({
        name: "",
        email: "",
        phoneNo: "",
        courseId: courses.length > 0 ? courses[0]?.id || "none" : "none",
        batchName: "none",
        courseUrl: "",
        courseIdValue: ""
      });
    };
  }, [courses]);

  // Reset form when modal opens/closes or when student changes
  useEffect(() => {
    if (open && student) {
      setFormData({
        name: student.name,
        email: student.email,
        phoneNo: student.phoneNo,
        courseId: student.courseId,
        batchName: student.batchName,
        courseUrl: student.courseUrl,
        courseIdValue: student.courseIdValue
      });
    } else if (open) {
      // For new student, reset to defaults
      setFormData({
        name: "",
        email: "",
        phoneNo: "",
        courseId: courses.length > 0 ? courses[0]?.id || "none" : "none",
        batchName: "none",
        courseUrl: "",
        courseIdValue: ""
      });
    }
  }, [open, student, courses]);

  // Get course name based on selected courseId
  const selectedCourse = courses.find(course => course.id === formData.courseId);
  
  // Filter batches based on selected course
  const filteredBatches = batches.filter(batch => batch.PK === selectedCourse?.title);
  
  const handleChange = (field, value) => {
    // Convert phoneNo to string if it's a number
    if (field === 'phoneNo') {
      value = String(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If course changes, reset batch selection
    if (field === 'courseId') {
      setFormData(prev => ({ ...prev, courseId: value, batchName: "none" }));
    }
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phoneNo || !formData.courseId || !formData.batchName) {
      return; // Don't submit if required fields are missing
    }
    
    const studentData = {
      ...formData,
      courseName: selectedCourse?.title || ""
    };
    
    if (isEditMode) {
      studentData.id = student.id;
    }
    
    onSave(studentData);
    onOpenChange(false);
  };

  const isFormValid = 
    formData.name.trim() !== "" && 
    formData.email.trim() !== "" && 
    String(formData.phoneNo).trim() !== "" &&
    formData.courseId !== "none" &&
    formData.batchName !== "none";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit Student" : "Add New Student"}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? "Update student's information." 
              : "Fill in the details to add a new student."}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="name">Name*</Label>
            <Input
              id="name"
              placeholder="Enter full name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="email">Email*</Label>
            <Input
              id="email"
              type="email"
              placeholder="Email address"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="phoneNo">Phone Number*</Label>
            <Input
              id="phoneNo"
              placeholder="Phone number"
              value={formData.phoneNo}
              onChange={(e) => handleChange('phoneNo', e.target.value)}
            />
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="courseId">Course*</Label>
            <Select 
              value={formData.courseId} 
              onValueChange={(value) => handleChange('courseId', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a course" />
              </SelectTrigger>
              <SelectContent>
                {courses.length > 0 ? (
                  courses.map(course => (
                    <SelectItem key={course.id} value={course.id}>
                      {course.title}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none">No courses available</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="batchName">Batch*</Label>
            <Select 
              value={formData.batchName} 
              onValueChange={(value) => handleChange('batchName', value)}
              disabled={!formData.courseId || formData.courseId === "none" || filteredBatches.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a batch" />
              </SelectTrigger>
              <SelectContent>
                {filteredBatches.length > 0 ? (
                  filteredBatches.map(batch => (
                    <SelectItem key={batch.SK} value={batch.SK}>
                      {batch.SK}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem value="none">No batches available for this course</SelectItem>
                )}
              </SelectContent>
            </Select>
            {formData.courseId && filteredBatches.length === 0 && (
              <p className="text-sm text-red-500">No batches available for this course. Please create a batch first.</p>
            )}
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="courseUrl">Course URL</Label>
            <Input
              id="courseUrl"
              placeholder="https://classplusapp.com/diy/courses/course-overview?id="
              value={formData.courseUrl || "https://classplusapp.com/diy/courses/course-overview?id="}
              onChange={(e) => handleChange('courseUrl', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">Base URL for the course</p>
          </div>
          
          <div className="grid gap-2">
            <Label htmlFor="courseIdValue">Course ID</Label>
            <Input
              id="courseIdValue"
              placeholder="Enter course ID"
              value={formData.courseIdValue || ""}
              onChange={(e) => handleChange('courseIdValue', e.target.value)}
            />
            <p className="text-xs text-muted-foreground">ID that will be appended to course URL</p>
          </div>
          
          {formData.courseUrl && formData.courseIdValue && (
            <div className="bg-gray-100 p-2 rounded-md text-sm">
              <p className="font-semibold">Preview:</p>
              <a 
                href={`${formData.courseUrl}${formData.courseIdValue}`} 
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {`${formData.courseUrl}${formData.courseIdValue}`}
              </a>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isFormValid}>
            {isEditMode ? "Update" : "Add"} Student
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

AddEditStudentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  student: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    phoneNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    courseId: PropTypes.string,
    batchName: PropTypes.string,
    courseUrl: PropTypes.string,
    courseIdValue: PropTypes.string
  }),
  batches: PropTypes.arrayOf(PropTypes.shape({
    SK: PropTypes.string.isRequired,
    courseId: PropTypes.string,
    courseUrl: PropTypes.string
  })).isRequired,
  courses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired
  })).isRequired,
  onSave: PropTypes.func.isRequired
};

AddEditStudentModal.defaultProps = {
  student: null,
  batches: []
}; 